/**
 * Staff Deployment Record (SDR) Service
 * Handles all SDR-related operations including staff leave records, 
 * substitute teacher assignments, lesson impact management, and calendar integration
 */

import { supabaseClient, supabaseAdmin } from '../../supabase/clients.js';

class SdrService {
  constructor() {
    this.client = supabaseClient;
    this.admin = supabaseAdmin;
  }

  /**
   * Get all teachers for SDR system
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of teacher objects
   */
  async getTeachers(tenantId = 'tn_01') {
    try {
      // Try to get real teacher data
      try {
        const { data, error } = await this.client
          .from('saas_teachers')
          .select(`
            id,
            teacher_name,
            teacher_id,
            email,
            phone,
            department,
            subjects,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('teacher_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_teachers table not found, generating mock data');
      }

      // Fallback: Generate mock teacher data
      return this.generateMockTeachers();
    } catch (error) {
      console.error('Error fetching teachers:', error);
      return this.generateMockTeachers();
    }
  }

  /**
   * Get all students for impact calculation
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudents(tenantId = 'tn_01') {
    try {
      // Try to get real student data
      try {
        const { data, error } = await this.client
          .from('saas_students')
          .select(`
            id,
            student_name,
            student_id,
            class_id,
            gender,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true })
          .order('student_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_students table not found, generating mock data');
      }

      // Fallback: Generate mock student data
      return this.generateMockStudents();
    } catch (error) {
      console.error('Error fetching students:', error);
      return this.generateMockStudents();
    }
  }

  /**
   * Get all SDR records
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of SDR record objects
   */
  async getSdrRecords(tenantId = 'tn_01') {
    try {
      // Try to get real SDR data
      try {
        const { data, error } = await this.client
          .from('saas_sdr_records')
          .select(`
            id,
            sdr_number,
            teacher_name,
            department,
            event_code,
            leave_type,
            activity_description,
            detailed_reason,
            start_date,
            end_date,
            start_time,
            end_time,
            affected_lessons,
            substitute_assignments,
            status,
            created_at,
            updated_at,
            tenant_id
          `)
          .eq('tenant_id', tenantId)
          .order('start_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_sdr_records table not found, generating mock data');
      }

      // Fallback: Generate mock SDR data
      return this.generateMockSdrRecords();
    } catch (error) {
      console.error('Error fetching SDR records:', error);
      return this.generateMockSdrRecords();
    }
  }

  /**
   * Get SDR records by date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of SDR record objects
   */
  async getSdrRecordsByDateRange(startDate, endDate, tenantId = 'tn_01') {
    try {
      // Try to get real SDR data
      try {
        const { data, error } = await this.client
          .from('saas_sdr_records')
          .select(`
            id,
            sdr_number,
            teacher_name,
            department,
            event_code,
            leave_type,
            activity_description,
            detailed_reason,
            start_date,
            end_date,
            start_time,
            end_time,
            affected_lessons,
            substitute_assignments,
            status,
            created_at,
            updated_at,
            tenant_id
          `)
          .eq('tenant_id', tenantId)
          .gte('start_date', startDate)
          .lte('end_date', endDate)
          .order('start_date', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_sdr_records table not found, generating mock data');
      }

      // Fallback: Generate mock SDR data for date range
      return this.generateMockSdrRecordsForDateRange(startDate, endDate);
    } catch (error) {
      console.error('Error fetching SDR records by date range:', error);
      return this.generateMockSdrRecordsForDateRange(startDate, endDate);
    }
  }

  /**
   * Get SDR statistics
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Statistics object
   */
  async getSdrStats(tenantId = 'tn_01') {
    try {
      const [teachers, students, records] = await Promise.all([
        this.getTeachers(tenantId),
        this.getStudents(tenantId),
        this.getSdrRecords(tenantId)
      ]);

      const totalTeachers = teachers.length;
      const totalStudents = students.length;
      const totalRecords = records.length;
      
      // Calculate total affected students
      const totalAffectedStudents = records.reduce((sum, record) => {
        if (record.affected_lessons && Array.isArray(record.affected_lessons)) {
          return sum + record.affected_lessons.reduce((lessonSum, lesson) => {
            return lessonSum + (lesson.student_count || 0);
          }, 0);
        }
        return sum;
      }, 0);

      // Status distribution
      const statusStats = {};
      records.forEach(record => {
        const status = record.status || 'draft';
        statusStats[status] = (statusStats[status] || 0) + 1;
      });

      // Department distribution
      const departmentStats = {};
      records.forEach(record => {
        const department = record.department;
        if (department) {
          departmentStats[department] = (departmentStats[department] || 0) + 1;
        }
      });

      // Leave type distribution
      const leaveTypeStats = {};
      records.forEach(record => {
        const leaveType = record.leave_type;
        if (leaveType) {
          leaveTypeStats[leaveType] = (leaveTypeStats[leaveType] || 0) + 1;
        }
      });

      return {
        totalTeachers,
        totalStudents,
        totalRecords,
        totalAffectedStudents,
        statusStats,
        departmentStats,
        leaveTypeStats,
        monthlyRecordCount: this.getMonthlyRecordCount(records)
      };
    } catch (error) {
      console.error('Error fetching SDR stats:', error);
      return {
        totalTeachers: 0,
        totalStudents: 0,
        totalRecords: 0,
        totalAffectedStudents: 0,
        statusStats: {},
        departmentStats: {},
        leaveTypeStats: {},
        monthlyRecordCount: {}
      };
    }
  }

  /**
   * Add new SDR record
   * @param {Object} recordData - SDR record data object
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Created SDR record object
   */
  async addSdrRecord(recordData, tenantId = 'tn_01') {
    try {
      // Generate SDR number
      const sdrNumber = this.generateSdrNumber();
      
      // Try to save to database
      try {
        const { data, error } = await this.client
          .from('saas_sdr_records')
          .insert([{
            ...recordData,
            sdr_number: sdrNumber,
            tenant_id: tenantId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_sdr_records table not found, saving to localStorage');
      }

      // Fallback: Save to localStorage
      const records = JSON.parse(localStorage.getItem('sdr_records') || '[]');
      const newRecord = {
        id: `sdr_${Date.now()}`,
        ...recordData,
        sdr_number: sdrNumber,
        tenant_id: tenantId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      records.push(newRecord);
      localStorage.setItem('sdr_records', JSON.stringify(records));
      return newRecord;
    } catch (error) {
      console.error('Error adding SDR record:', error);
      throw error;
    }
  }

  /**
   * Update existing SDR record
   * @param {string} recordId - SDR record identifier
   * @param {Object} recordData - Updated SDR record data
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Updated SDR record object
   */
  async updateSdrRecord(recordId, recordData, tenantId = 'tn_01') {
    try {
      // Try to update in database
      try {
        const { data, error } = await this.client
          .from('saas_sdr_records')
          .update({
            ...recordData,
            updated_at: new Date().toISOString()
          })
          .eq('id', recordId)
          .eq('tenant_id', tenantId)
          .select()
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_sdr_records table not found, updating localStorage');
      }

      // Fallback: Update in localStorage
      const records = JSON.parse(localStorage.getItem('sdr_records') || '[]');
      const index = records.findIndex(record => record.id === recordId);
      if (index !== -1) {
        records[index] = {
          ...records[index],
          ...recordData,
          updated_at: new Date().toISOString()
        };
        localStorage.setItem('sdr_records', JSON.stringify(records));
        return records[index];
      }
      throw new Error('SDR record not found');
    } catch (error) {
      console.error('Error updating SDR record:', error);
      throw error;
    }
  }

  /**
   * Delete SDR record
   * @param {string} recordId - SDR record identifier
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<boolean>} Success status
   */
  async deleteSdrRecord(recordId, tenantId = 'tn_01') {
    try {
      // Try to delete from database
      try {
        const { error } = await this.client
          .from('saas_sdr_records')
          .delete()
          .eq('id', recordId)
          .eq('tenant_id', tenantId);

        if (!error) {
          return true;
        }
      } catch (tableError) {
        console.log('saas_sdr_records table not found, deleting from localStorage');
      }

      // Fallback: Delete from localStorage
      const records = JSON.parse(localStorage.getItem('sdr_records') || '[]');
      const filteredRecords = records.filter(record => record.id !== recordId);
      localStorage.setItem('sdr_records', JSON.stringify(filteredRecords));
      return true;
    } catch (error) {
      console.error('Error deleting SDR record:', error);
      throw error;
    }
  }

  /**
   * Get lesson periods configuration
   * @returns {Array} Array of lesson period objects
   */
  getLessonPeriods() {
    return [
      { id: 1, name: '第1節', time: '08:30-09:15', subjects: ['中文', '英文', '數學'] },
      { id: 2, name: '第2節', time: '09:15-10:00', subjects: ['中文', '英文', '數學'] },
      { id: 3, name: '第3節', time: '10:15-11:00', subjects: ['中文', '英文', '數學'] },
      { id: 4, name: '第4節', time: '11:00-11:45', subjects: ['中文', '英文', '數學'] },
      { id: 5, name: '第5節', time: '12:30-13:15', subjects: ['中文', '英文', '數學'] },
      { id: 6, name: '第6節', time: '13:15-14:00', subjects: ['中文', '英文', '數學'] },
      { id: 7, name: '第7節', time: '14:00-14:45', subjects: ['中文', '英文', '數學'] },
      { id: 8, name: '第8節', time: '14:45-15:30', subjects: ['中文', '英文', '數學'] }
    ];
  }

  /**
   * Get department list
   * @returns {Array} Array of department objects
   */
  getDepartments() {
    return [
      { id: 'chinese', name: '中文科' },
      { id: 'english', name: '英文科' },
      { id: 'mathematics', name: '數學科' },
      { id: 'chemistry', name: '化學科' },
      { id: 'physics', name: '物理科' },
      { id: 'biology', name: '生物科' },
      { id: 'history', name: '歷史科' },
      { id: 'geography', name: '地理科' },
      { id: 'economics', name: '經濟科' },
      { id: 'music', name: '音樂科' },
      { id: 'pe', name: '體育科' },
      { id: 'va', name: '視覺藝術科' },
      { id: 'ict', name: '資訊科技科' },
      { id: 'ls', name: '通識教育科' },
      { id: 'support', name: '學生支援組' }
    ];
  }

  /**
   * Get leave types
   * @returns {Array} Array of leave type objects
   */
  getLeaveTypes() {
    return [
      { id: 'training', name: '進修培訓' },
      { id: 'business', name: '公幹' },
      { id: 'personal', name: '事假' },
      { id: 'sick', name: '病假' },
      { id: 'meeting', name: '會議' }
    ];
  }

  /**
   * Generate unique SDR number
   * @returns {string} SDR number
   */
  generateSdrNumber() {
    const year = new Date().getFullYear();
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `SDR${year}${month}${random}`;
  }

  // Helper methods for statistics
  getMonthlyRecordCount(records) {
    const monthlyCount = {};
    records.forEach(record => {
      const month = record.start_date ? record.start_date.substring(0, 7) : 'Unknown';
      monthlyCount[month] = (monthlyCount[month] || 0) + 1;
    });
    return monthlyCount;
  }

  // Mock data generation methods
  generateMockTeachers() {
    const departments = this.getDepartments();
    const teachers = [
      { name: '陳志明', id: 'T001', department: 'chinese', subjects: ['中文'] },
      { name: '李美玲', id: 'T002', department: 'english', subjects: ['英文'] },
      { name: '王建華', id: 'T003', department: 'mathematics', subjects: ['數學'] },
      { name: '張淑芬', id: 'T004', department: 'chemistry', subjects: ['化學'] },
      { name: '劉文傑', id: 'T005', department: 'physics', subjects: ['物理'] },
      { name: '黃雅婷', id: 'T006', department: 'biology', subjects: ['生物'] },
      { name: '林志強', id: 'T007', department: 'history', subjects: ['歷史'] },
      { name: '吳淑娟', id: 'T008', department: 'geography', subjects: ['地理'] },
      { name: '鄭建民', id: 'T009', department: 'economics', subjects: ['經濟'] },
      { name: '何美惠', id: 'T010', department: 'music', subjects: ['音樂'] },
      { name: '蔡志豪', id: 'T011', department: 'pe', subjects: ['體育'] },
      { name: '許雅文', id: 'T012', department: 'va', subjects: ['視覺藝術'] },
      { name: '謝建華', id: 'T013', department: 'ict', subjects: ['資訊科技'] },
      { name: '羅淑芬', id: 'T014', department: 'ls', subjects: ['通識教育'] },
      { name: '葉志明', id: 'T015', department: 'support', subjects: ['學生支援'] }
    ];

    return teachers.map((teacher, index) => {
      const department = departments.find(d => d.id === teacher.department);
      return {
        id: `teacher_${teacher.id}`,
        teacher_name: teacher.name,
        teacher_id: teacher.id,
        email: `${teacher.name.toLowerCase().replace(/\s/g, '')}@school.edu.hk`,
        phone: `+852-${Math.floor(Math.random() * 90000000) + 20000000}`,
        department: department ? department.name : '未知部門',
        subjects: teacher.subjects,
        tenant_id: 'tn_01',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    });
  }

  generateMockStudents() {
    const students = [];
    const classes = ['4A', '4B', '5A', '5B', '6A', '6B'];
    const names = [
      '陳綺婷', '錢嘉欣', '李嘉蔚', '張志明', '王美玲', '劉建華',
      '黃淑芬', '林志強', '吳雅婷', '鄭文傑', '周美惠', '蔡志明',
      '許淑芬', '謝建華', '羅雅婷', '葉志強', '何美玲', '高建民',
      '梁淑娟', '蘇志豪', '范美惠', '江文傑', '方雅婷', '石志強',
      '余美玲', '鄧建華', '薛淑芬', '韓志明', '馮雅文', '曾志豪'
    ];

    classes.forEach((classId, classIndex) => {
      for (let i = 1; i <= 5; i++) {
        const nameIndex = (classIndex * 5 + i - 1) % names.length;
        students.push({
          id: `student_${classId}${i.toString().padStart(2, '0')}`,
          student_name: names[nameIndex],
          student_id: `${classId}${i.toString().padStart(2, '0')}`,
          class_id: classId,
          gender: Math.random() > 0.5 ? 'M' : 'F',
          tenant_id: 'tn_01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    });

    return students;
  }

  generateMockSdrRecords() {
    const records = [];
    const teachers = this.generateMockTeachers();
    const departments = this.getDepartments();
    const leaveTypes = this.getLeaveTypes();
    const lessonPeriods = this.getLessonPeriods();
    
    const activities = [
      '教師專業發展培訓課程',
      '教育局會議',
      '學校行政會議',
      '學科組會議',
      '家長會會議',
      '校外考察活動',
      '專業進修課程',
      '學校發展計劃會議',
      '課程檢討會議',
      '學生輔導會議'
    ];

    // Generate records for the current year
    const currentYear = new Date().getFullYear();
    const months = [1, 2, 3, 4, 5, 6, 9, 10, 11, 12]; // School months

    months.forEach(month => {
      const daysInMonth = new Date(currentYear, month, 0).getDate();
      const recordsPerMonth = Math.floor(Math.random() * 5) + 3; // 3-7 records per month

      for (let i = 0; i < recordsPerMonth; i++) {
        const day = Math.floor(Math.random() * daysInMonth) + 1;
        const teacher = teachers[Math.floor(Math.random() * teachers.length)];
        const department = departments.find(d => d.name === teacher.department);
        const leaveType = leaveTypes[Math.floor(Math.random() * leaveTypes.length)];
        const activity = activities[Math.floor(Math.random() * activities.length)];
        
        const startDate = `${currentYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        const endDate = startDate; // Single day records for simplicity
        
        // Generate affected lessons
        const affectedLessons = [];
        const numLessons = Math.floor(Math.random() * 4) + 1; // 1-4 lessons
        const selectedPeriods = lessonPeriods.sort(() => 0.5 - Math.random()).slice(0, numLessons);
        
        selectedPeriods.forEach(period => {
          const classes = ['4A', '4B', '5A', '5B', '6A', '6B'];
          const selectedClass = classes[Math.floor(Math.random() * classes.length)];
          const subjects = ['中文', '英文', '數學', '化學', '物理', '生物', '歷史', '地理'];
          const subject = subjects[Math.floor(Math.random() * subjects.length)];
          
          affectedLessons.push({
            period_id: period.id,
            period_name: period.name,
            time: period.time,
            subject: subject,
            class_name: selectedClass,
            student_count: Math.floor(Math.random() * 20) + 20, // 20-40 students
            classroom: `Room ${Math.floor(Math.random() * 20) + 1}`,
            substitute_teacher: teachers[Math.floor(Math.random() * teachers.length)].teacher_name,
            status: Math.random() > 0.3 ? 'assigned' : 'pending'
          });
        });

        const statuses = ['draft', 'pending', 'approved'];
        const status = statuses[Math.floor(Math.random() * statuses.length)];

        records.push({
          id: `sdr_${currentYear}_${month}_${day}_${i}`,
          sdr_number: this.generateSdrNumber(),
          teacher_name: teacher.teacher_name,
          department: department ? department.name : '未知部門',
          event_code: `EVT${Math.floor(Math.random() * 1000)}`,
          leave_type: leaveType.name,
          activity_description: activity,
          detailed_reason: `${activity}的詳細說明，包括活動目的、地點和預期成果。`,
          start_date: startDate,
          end_date: endDate,
          start_time: '09:00',
          end_time: '17:00',
          affected_lessons: affectedLessons,
          substitute_assignments: affectedLessons.map(lesson => ({
            lesson_id: lesson.period_id,
            substitute_teacher: lesson.substitute_teacher,
            status: lesson.status
          })),
          status: status,
          tenant_id: 'tn_01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    });

    return records;
  }

  generateMockSdrRecordsForDateRange(startDate, endDate) {
    const allRecords = this.generateMockSdrRecords();
    return allRecords.filter(record => 
      record.start_date >= startDate && record.start_date <= endDate
    );
  }
}

// Create and export service instance
export const sdrService = new SdrService();
export { SdrService };
